import {
  AGI_HASTE,
  AGI_SPEED,
  GREY_LEVEL_GAP,
  MAX_HASTE,
  STR_ATTACK,
  VIT_HEALTH,
  XP_ABOVE_LEVEL_BONUS,
  XP_ABOVE_LEVEL_CAP,
} from './constants';
import { getClass } from './classes';
import type { ClassId } from './types';

export interface Attributes {
  str: number;
  agi: number;
  vit: number;
}

export interface DerivedStats {
  maxHp: number;
  maxMp: number;
  mpRegen: number;
  attack: number;
  defense: number;
  speed: number;
  range: number;
  cooldown: number;
  critChance: number;
}

/**
 * XP required to advance from `level` to `level + 1`.
 *
 * A steep power curve: every level costs more than the last, and because a
 * character's total progress is the running sum of these, reaching level 100 is
 * an old-school grind rather than an afternoon. Tune the exponent outwards for
 * a harsher or gentler climb.
 */
export function xpToNext(level: number): number {
  return Math.round(85 * Math.pow(level, 2.3));
}

/**
 * How much a kill is worth to a character, based on the level gap.
 * Monsters far below you pay nothing; monsters above you pay a bonus, so
 * pushing into dangerous country is the productive way to level.
 */
export function xpLevelFactor(playerLevel: number, enemyLevel: number): number {
  const gap = enemyLevel - playerLevel;
  if (gap <= -GREY_LEVEL_GAP) return 0;
  if (gap >= 0) return 1 + Math.min(XP_ABOVE_LEVEL_CAP, gap * XP_ABOVE_LEVEL_BONUS);
  return (GREY_LEVEL_GAP + gap) / GREY_LEVEL_GAP;
}

export function deriveStats(classId: ClassId, level: number, attrs: Attributes): DerivedStats {
  const c = getClass(classId);
  const levels = Math.max(0, level - 1);
  const haste = Math.min(MAX_HASTE, attrs.agi * AGI_HASTE);
  return {
    maxHp: Math.round(c.base.maxHp + c.growth.maxHp * levels + attrs.vit * VIT_HEALTH),
    maxMp: Math.round(c.base.maxMp + c.growth.maxMp * levels),
    mpRegen: c.mpRegen,
    attack: c.base.attack + c.growth.attack * levels + attrs.str * STR_ATTACK,
    defense: c.base.defense + c.growth.defense * levels,
    speed: c.speed + attrs.agi * AGI_SPEED,
    range: c.attack.range,
    cooldown: c.attack.cooldown * (1 - haste),
    critChance: 0.12,
  };
}
